import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Separator,
  Text,
  useIsMobile,
  type BulkAction,
} from "@salestrak/ui";
import { PageHeader } from "#app/components/page-header";
import { MembersTable, buildMembersFilterDefs } from "#app/components/members/members-table";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { MEMBER_LIST } from "#app/lib/filters/list-configs";
import { useAppData, type MemberRow, type RoleRow, type TeamOption } from "#app/lib/data";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { useSetReportsTo } from "#app/lib/hooks/use-set-reports-to";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePermission } from "#app/lib/hooks/use-permission";
import { settleAll } from "#app/lib/utils/settle-all";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useTableFilters } from "#app/lib/hooks/use-table-filters";
import { withPermission } from "#app/components/permissions/require-permission";

function SettingsMembersPage() {
  const isMobile = useIsMobile();
  const { push } = useAppNavigate();

  // Sibling data extracted from the ungrouped query.
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const handleSiblingData = useCallback((r: RoleRow[], t: TeamOption[]) => {
    setRoles(r);
    setTeams(t);
  }, []);

  const filterDefs = useMemo(() => buildMembersFilterDefs(teams, roles), [teams, roles]);
  const memberFilters = useTableFilters(filterDefs);

  const { searchValue, setSearchValue, sorting, setSorting, searchWhere, orderBy, where } =
    useListQueryState(MEMBER_LIST, { filterWhere: memberFilters.where });

  const { updateMember, updateMemberRoles, updateMemberTeams, resendInvite, cancelInvite } = useMemberMutations();
  const { addTeamMembers, removeTeamMembers } = useTeamMutations();
  const canUpdateBase = usePermission("manage.users");
  const canUpdateRoles = usePermission("manage.users");
  const canInvite = usePermission("manage.users");
  // The Teams cell writes through add/removeTeamMembers, which the server
  // gates PER TEAM: manage.teams OR managing that specific team. The cell's
  // picker is scoped to exactly the teams the viewer can manage (the
  // server-computed `viewerCanManage`, already on the workspace team map), so
  // every offered toggle is one the server will accept. manage.teams holders
  // see every team; a team manager sees only theirs; no manageable team =
  // read-only display rather than a silently failing editor.
  const { teams: workspaceTeams } = useWorkspaceData();
  const manageableTeams = useMemo(
    () => workspaceTeams.filter((t) => t.viewerCanManage),
    [workspaceTeams],
  );
  const manageableTeamIds = useMemo(
    () => new Set(manageableTeams.map((t) => t.teamId)),
    [manageableTeams],
  );

  const [inviteOpen, setInviteOpen] = useState(false);

  // The viewer's own tier (higher number = more privilege, the server's
  // SYSTEM_ROLE_HIERARCHY). Roles above it can be neither assigned nor
  // revoked, so the bulk picker lists only what the server will accept and
  // the action stands down while a selected member holds a higher role.
  const { currentUser } = useAppData();
  const viewerId = currentUser?.userId;
  const viewerTier = useMemo(
    () => Math.max(0, ...(currentUser?.roles ?? []).map((r) => r.hierarchy)),
    [currentUser],
  );
  const roleTier = useMemo(
    () => new Map(roles.map((r) => [r.roleId, r.hierarchy])),
    [roles],
  );
  const assignableRoles = useMemo(
    () => roles.filter((r) => r.hierarchy <= viewerTier),
    [roles, viewerTier],
  );

  // --- Bulk actions config (settings variant only) ---
  // Selection rules mirror the server's: a selected row the viewer may not
  // touch DISABLES the action with the reason (`disabledReason`); a row the
  // action simply doesn't apply to (already suspended, not invited) is
  // skipped, and the confirm copy and toast count the rows it will reach.
  // The viewer's own row is skipped by Suspend — leaving is its own flow.
  const bulkActions = useMemo<BulkAction<MemberRow>[]>(() => {
    const actions: BulkAction<MemberRow>[] = [];
    const plural = (n: number, noun: string) => `${n} ${noun}${n !== 1 ? "s" : ""}`;
    // The members a role pick changes: not those who hold exactly that role.
    const needsRole = (rows: MemberRow[], roleId: string | undefined) =>
      rows.filter((m) => !(m.roles.length === 1 && m.roles[0]!.roleId === roleId));

    if (canUpdateRoles && assignableRoles.length > 0) {
      actions.push({
        id: "change-role",
        type: "option-picker",
        label: "Role",
        dialogLabel: "Change Role...",
        icon: "shield",
        options: assignableRoles.map((r) => ({ id: r.roleId, label: r.roleName })),
        disabledReason: (rows) =>
          rows.some((m) =>
            m.roles.some((r) => (roleTier.get(r.roleId) ?? 0) > viewerTier),
          )
            ? "Includes a role above yours"
            : undefined,
        // A member who already holds exactly this role is not written, and
        // not counted.
        successMessage: (rows, _count, roleId) =>
          `Role updated for ${plural(needsRole(rows, roleId).length, "member")}`,
        onAction: async (selected, roleId) => {
          await settleAll(needsRole(selected, roleId), (m) => {
            const current = m.roles.map((r) => r.roleId);
            return updateMemberRoles(
              m.userId,
              { add: [roleId], remove: current.filter((id) => id !== roleId) },
              { silent: true },
            );
          });
        },
      });
    }

    if (canUpdateBase) {
      const suspendable = (rows: MemberRow[]) =>
        rows.filter((m) => m.memberStatus === "active" && m.userId !== viewerId);
      actions.push({
        id: "suspend",
        type: "confirm",
        label: "Suspend",
        dialogLabel: "Suspend...",
        icon: "x-circle",
        destructive: true,
        hidden: (rows) => !rows.some((m) => m.memberStatus === "active"),
        disabledReason: (rows) =>
          suspendable(rows).length === 0 ? "You can't suspend yourself" : undefined,
        confirmTitle: (rows) => `Suspend ${plural(suspendable(rows).length, "member")}?`,
        confirmDescription: (rows) =>
          `Suspended members will lose access to this workspace.${
            rows.some((m) => m.userId === viewerId) ? " You're not included." : ""
          }`,
        confirmLabel: "Suspend",
        successMessage: (rows) => `${plural(suspendable(rows).length, "member")} suspended`,
        onAction: async (selected) => {
          await settleAll(suspendable(selected), (m) =>
            updateMember(m.userId, { memberStatus: "suspended" }, { silent: true }),
          );
        },
      });

      const reactivatable = (rows: MemberRow[]) =>
        rows.filter((m) => m.memberStatus === "suspended");
      actions.push({
        id: "reactivate",
        type: "direct",
        label: "Reactivate",
        icon: "play-circle",
        hidden: (rows) => reactivatable(rows).length === 0,
        successMessage: (rows) =>
          `${plural(reactivatable(rows).length, "member")} reactivated`,
        onAction: async (selected) => {
          await settleAll(reactivatable(selected), (m) =>
            updateMember(m.userId, { memberStatus: "active" }, { silent: true }),
          );
        },
      });
    }

    if (canInvite) {
      const invited = (rows: MemberRow[]) =>
        rows.filter((m) => m.memberStatus === "invited");
      actions.push({
        id: "resend-invite",
        type: "direct",
        label: "Resend Invite",
        icon: "send",
        hidden: (rows) => invited(rows).length === 0,
        successMessage: (rows) => `${plural(invited(rows).length, "invite")} resent`,
        onAction: async (selected) => {
          await settleAll(invited(selected), (m) => resendInvite(m.userId, { silent: true }));
        },
      });

      actions.push({
        id: "cancel-invite",
        type: "confirm",
        label: "Cancel Invite",
        dialogLabel: "Cancel Invites...",
        icon: "x-circle",
        destructive: true,
        hidden: (rows) => !rows.some((m) => m.memberStatus === "invited"),
        confirmTitle: (rows) => {
          const count = rows.filter((m) => m.memberStatus === "invited").length;
          return `Cancel ${count} invite${count !== 1 ? "s" : ""}?`;
        },
        confirmDescription: () =>
          "Invitees will be removed from the workspace and their invitation links will stop working.",
        confirmLabel: "Cancel Invite",
        successMessage: (rows) => `${plural(invited(rows).length, "invite")} canceled`,
        onAction: async (selected) => {
          await settleAll(invited(selected), (m) => cancelInvite(m.userId, { silent: true }));
        },
      });
    }

    // Team membership is gated PER TEAM by the server (manage.teams OR
    // managing that team), so the options are exactly the manageable teams —
    // the same scoping rule the Teams cell's picker follows. Both live in the
    // More dialog: a drill-in pick, not a strip button.
    if (manageableTeams.length > 0) {
      actions.push({
        id: "add-to-team",
        type: "option-picker",
        label: "Add to Team",
        dialogLabel: "Add to Team...",
        icon: "user-plus",
        overflow: true,
        successMessage: (_rows, count) =>
          `${count} member${count !== 1 ? "s" : ""} added to the team`,
        options: manageableTeams.map((t) => ({ id: t.teamId, label: t.teamName })),
        onAction: async (selected, teamId) => {
          await addTeamMembers(
            teamId,
            selected.map((m) => m.userId),
            "member",
            "members_bulk",
            { silent: true },
          );
        },
      });
      actions.push({
        id: "remove-from-team",
        type: "option-picker",
        label: "Remove from Team",
        dialogLabel: "Remove from Team...",
        icon: "user",
        overflow: true,
        successMessage: (_rows, count) =>
          `${count} member${count !== 1 ? "s" : ""} removed from the team`,
        options: manageableTeams.map((t) => ({ id: t.teamId, label: t.teamName })),
        onAction: async (selected, teamId) => {
          await removeTeamMembers(
            teamId,
            selected.map((m) => m.userId),
            { silent: true },
          );
        },
      });
    }

    return actions;
  }, [
    canUpdateBase,
    canUpdateRoles,
    canInvite,
    assignableRoles,
    roleTier,
    viewerTier,
    viewerId,
    updateMember,
    updateMemberRoles,
    resendInvite,
    cancelInvite,
    manageableTeams,
    addTeamMembers,
    removeTeamMembers,
  ]);

  const handleTeamsChange = useCallback(
    async (userId: string, currentTeamIds: string[], newTeamIds: string[]) => {
      await updateMemberTeams(userId, currentTeamIds, newTeamIds);
    },
    [updateMemberTeams]
  );

  // Reports-to — the same write every mount of the column commits (null
  // clears; a pick from the member's own line lifts them up). Requires
  // manage.users, which this whole route already holds.
  const setReportsTo = useSetReportsTo("settings_members");
  const handleManagerChange = useCallback(
    async (userId: string, managerId: string | null) => {
      await setReportsTo(userId, managerId);
    },
    [setReportsTo]
  );

  const handleRoleChange = useCallback(
    async (userId: string, currentRoleIds: string[], roleId: string) => {
      const isSelected = currentRoleIds.includes(roleId);
      const add = isSelected ? [] : [roleId];
      const remove = isSelected ? [roleId] : currentRoleIds.filter((id) => id !== roleId);
      await updateMemberRoles(userId, { add, remove });
    },
    [updateMemberRoles]
  );

  const handleNavigateToProfile = useCallback(
    (userId: string) => {
      push(`/settings/members/${userId}`);
    },
    [push]
  );

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="Members"
        actions={
          !isMobile && canInvite ? (
            <Button size="xs" onClick={() => setInviteOpen(true)}>
              <Text size={null}>Invite</Text>
            </Button>
          ) : undefined
        }
      />
      {!isMobile && <Separator />}
      <MembersTable
        variant="settings"
        filterState={memberFilters}
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        availableTeams={teams}
        availableRoles={roles}
        onNavigateToProfile={handleNavigateToProfile}
        onTeamsChange={manageableTeamIds.size > 0 ? handleTeamsChange : undefined}
        manageableTeamIds={manageableTeamIds}
        onManagerChange={handleManagerChange}
        onRoleChange={handleRoleChange}
        searchWhere={searchWhere}
        onSiblingData={handleSiblingData}
        bulkActions={bulkActions}
        actionButton={
          isMobile && canInvite ? (
            // The word stays on phones too: an icon-only user-plus glyph in
            // the toolbar read as an unlabeled mystery, not as Invite.
            <Button size="xs" onClick={() => setInviteOpen(true)}>
              <Text size={null}>Invite</Text>
            </Button>
          ) : undefined
        }
      />
      {canInvite && (
        <InviteMembersDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
        />
      )}
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/members/")({
  component: withPermission("manage.users", SettingsMembersPage),
});
